import { useEffect, useRef } from "react";
import { type Address, type State, inject } from "../ng";

export function MapLocation() {
	const ref = useRef<HTMLSpanElement>(null);

	useEffect(() => {
		const span = ref.current;
		if (!span) {
			return;
		}
		const map = inject("mapService").getMap();
		if (!map.div.isConnected) {
			return;
		}

		const UNKNOWN = "Location Unknown";

		const alkGeocodeService = inject("alkGeocodeService");
		const user = inject("UserProvider").getUser();
		const naStates = inject("STATES");
		const naStatesMap = new Map(naStates.map((x) => [x.abbreviation, x]));
		const p1 = new window.ALKMaps.Projection("EPSG:900913");
		const p2 = new window.ALKMaps.Projection("EPSG:4326");

		const options = {
			region: user.region,
			dataset: user.dataVersion,
		};

		const listener = async () => {
			const center = map.getCenter().transform(p1, p2);
			await alkGeocodeService
				.reverseGeocode(center, options)
				.then((matches) => {
					if (matches) {
						return addressToBreadcrumbs(
							matches[0].Address,
							user.region,
							map.getZoom(),
							naStatesMap,
						);
					}
					return UNKNOWN;
				})
				.catch(() => UNKNOWN)
				.then((breadcrumbs) => {
					span.textContent = breadcrumbs;
				});
		};

		listener();
		map.events.register("moveend", undefined, listener);
		return () => {
			map.events.unregister("moveend", undefined, listener);
		};
	}, []);

	return <span ref={ref} />;
}

export function addressToBreadcrumbs(
	address: Address,
	region: string,
	zoom: number,
	naStatesMap: Map<string, State>,
) {
	const city = `${address.City} > `;
	let county = "";

	if (region === "NA") {
		county = address.County ? `${address.County} County > ` : "";
	} else {
		county = address.County ? `${address.County} > ` : "";
	}

	let state = "";
	let country = "";
	let breadcrumbs = "";

	if (region === "NA") {
		const s = naStatesMap.get(address.State);
		if (s) {
			state = `${s.name} > `;
			country = s.country;
		}
	} else {
		// Other regions just use the country. todo: make logic more clear and document logic for what should be displayed for different regions
		country = address.Country;
	}

	switch (true) {
		case zoom >= 13:
			breadcrumbs = city + county + state + country;
			break;
		case zoom >= 10:
			breadcrumbs = county + state + country;
			break;
		case zoom >= 7:
			breadcrumbs = state + country;
			break;
		case zoom < 7:
			breadcrumbs = country;
	}

	return breadcrumbs;
}
